'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import KpiCards from './components/KpiCards';
import ExpiryTimeline from './components/ExpiryTimeline';
import CertificateReadinessCard from './components/CertificateReadinessCard';
import CertificateTable from './components/CertificateTable';
import ProfileCompleteness from './components/ProfileCompleteness';
import CertificateInsights from './components/CertificateInsights';
import UploadBanner from './components/UploadBanner';
import DashboardEmptyState from './components/DashboardEmptyState';
import MobileHomeDashboard from './components/MobileHomeDashboard';
import { useCertificates } from '@/contexts/CertificatesContext';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';

function DashboardContent() {
  const { certificates, isLoading, lastSyncedAt, syncFromProfile } = useCertificates();
  const [syncing, setSyncing] = useState(false);

  const syncLabel = lastSyncedAt
    ? `Last synced · ${new Date(lastSyncedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
    : 'Upload your DG profile PDF to get started';

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await syncFromProfile();
      if (result) toast.success(`Synced ${result.count} certificates from your DG profile`);
      else toast.error('No profile data found — upload your DG profile PDF first');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <AppLayout activeTab="dashboard">
      {/* Mobile: Seamigo design Home dashboard */}
      <div className="md:hidden fade-in">
        <MobileHomeDashboard />
      </div>

      {/* Desktop / tablet: existing rich dashboard layout */}
      <div className="hidden md:block space-y-5 pb-4 fade-in">
        <div className="dashboard-hero flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 pt-1">
          <div className="min-w-0">
            <p className="dashboard-kicker hidden md:block">Seamigo workspace</p>
            <h1 className="text-2xl font-700 text-foreground tracking-tight">My Certificates</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{syncLabel}</p>
          </div>
          <div className="grid grid-cols-[auto,1fr] sm:flex sm:items-center gap-2 shrink-0 w-full sm:w-auto">
            <button
              onClick={handleSync}
              disabled={syncing}
              className="app-secondary-action flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-600 text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
              title="Rebuild certificate list from your stored DG profile data"
            >
              <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} />
              <span>Sync</span>
            </button>
            {certificates.length > 0 && <UploadBanner compact />}
          </div>
        </div>

        {!isLoading && certificates.length === 0 ? (
          <DashboardEmptyState />
        ) : (
          <>
            <CertificateReadinessCard />
            <KpiCards />
            <ExpiryTimeline />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2">
                <CertificateTable />
              </div>
              <div className="lg:col-span-1">
                <ProfileCompleteness />
                <div className="mt-4">
                  <CertificateInsights />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}

export default function DashboardPage() {
  return <DashboardContent />;
}
